import React, { useState, useEffect } from 'react';
import { auditApi, usersApi } from '../../lib/api';
import DashboardLayout from '../../components/DashLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Activity,
  Search,
  Filter,
  Eye,
  Loader2,
  Clock,
  User,
  RefreshCw,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    actor_id: 'all',
    entity_type: 'all',
    action: 'all',
  });
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.actor_id !== 'all') params.actor_id = filters.actor_id;
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.action !== 'all') params.action = filters.action;

      const [logsRes, usersRes] = await Promise.all([
        auditApi.list({ ...params, limit: 200 }),
        usersApi.list({ include_disabled: true }),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const getActionBadge = (action) => {
    const actionColors = {
      login: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      create_user: 'bg-green-500/20 text-green-400 border-green-500/30',
      create_project: 'bg-green-500/20 text-green-400 border-green-500/30',
      create_task: 'bg-green-500/20 text-green-400 border-green-500/30',
      update_user: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      update_project: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      update_task: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      delete_user: 'bg-red-500/20 text-red-400 border-red-500/30',
      delete_project: 'bg-red-500/20 text-red-400 border-red-500/30',
      delete_task: 'bg-red-500/20 text-red-400 border-red-500/30',
      change_password: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      reset_password: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };

    const colorClass = actionColors[action] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    const formattedAction = action
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return <Badge className={colorClass}>{formattedAction}</Badge>;
  };

  const getEntityBadge = (entityType) => {
    const colors = {
      user: 'bg-blue-500/20 text-blue-400',
      project: 'bg-green-500/20 text-green-400',
      task: 'bg-amber-500/20 text-amber-400',
    };

    return (
      <Badge variant="outline" className={colors[entityType] || 'bg-gray-500/20 text-gray-400'}>
        {entityType}
      </Badge>
    );
  };

  const uniqueActions = [...new Set(logs.map((l) => l.action))];
  const uniqueEntityTypes = [...new Set(logs.map((l) => l.entity_type))];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2">
              <Activity className="w-8 h-8 text-amber-500" />
              Audit Logs
            </h1>
            <p className="text-muted-foreground mt-1">Track all system activity and changes</p>
          </div>
          <Button
            onClick={loadData}
            variant="outline"
            className="border-[#27272A]"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-[#0A0A0B] border-[#27272A]">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4">
              <div className="w-[200px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Actor</Label>
                <Select value={filters.actor_id} onValueChange={(v) => setFilters({ ...filters, actor_id: v })}>
                  <SelectTrigger className="bg-[#121214] border-[#27272A]" data-testid="filter-actor">
                    <SelectValue placeholder="All actors" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    <SelectItem value="all">All Actors</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[180px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Entity Type</Label>
                <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v })}>
                  <SelectTrigger className="bg-[#121214] border-[#27272A]" data-testid="filter-entity-type">
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    <SelectItem value="all">All Entities</SelectItem>
                    {uniqueEntityTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[180px]">
                <Label className="text-xs text-muted-foreground mb-1 block">Action</Label>
                <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                  <SelectTrigger className="bg-[#121214] border-[#27272A]" data-testid="filter-action">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-[#27272A]">
                    <SelectItem value="all">All Actions</SelectItem>
                    {uniqueActions.map((action) => (
                      <SelectItem key={action} value={action}>
                        {action
                          .split('_')
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-[#0A0A0B] border-[#27272A]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#27272A] hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Time</TableHead>
                    <TableHead className="text-muted-foreground">Actor</TableHead>
                    <TableHead className="text-muted-foreground">Action</TableHead>
                    <TableHead className="text-muted-foreground">Entity</TableHead>
                    <TableHead className="text-muted-foreground">IP</TableHead>
                    <TableHead className="text-right text-muted-foreground">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow
                        key={log.id}
                        className="border-[#27272A] hover:bg-white/5"
                        data-testid={`audit-log-${log.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="mono text-muted-foreground">
                              {format(parseISO(log.created_at), 'PP p')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-primary" />
                            <span className="text-white">{log.actor_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getEntityBadge(log.entity_type)}
                            <code className="text-xs text-muted-foreground">{log.entity_id.slice(0, 8)}...</code>
                          </div>
                        </TableCell>
                        <TableCell className="mono text-sm text-muted-foreground">
                          {log.ip || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {(log.before_json || log.after_json) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedLog(log)}
                              className="h-8 w-8 text-muted-foreground hover:text-white"
                              data-testid={`view-log-${log.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Log Details Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
          <DialogContent className="bg-[#121214] border-[#27272A] sm:max-w-[600px]" data-testid="log-details-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Audit Log Details</DialogTitle>
              <DialogDescription>
                {selectedLog &&
                  `${selectedLog.action.replace('_', ' ')} on ${selectedLog.entity_type} by ${selectedLog.actor_name}`}
              </DialogDescription>
            </DialogHeader>

            {selectedLog && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Timestamp</Label>
                    <p className="text-white mono">{format(parseISO(selectedLog.created_at), 'PPpp')}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">IP Address</Label>
                    <p className="text-white mono">{selectedLog.ip || 'N/A'}</p>
                  </div>
                </div>

                {selectedLog.before_json && (
                  <div>
                    <Label className="text-muted-foreground mb-2 block">Before</Label>
                    <ScrollArea className="h-[120px] rounded-lg bg-[#0A0A0B] border border-[#27272A] p-3">
                      <pre className="text-xs text-amber-400 mono whitespace-pre-wrap">
                        {JSON.stringify(JSON.parse(selectedLog.before_json), null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

                {selectedLog.after_json && (
                  <div>
                    <Label className="text-muted-foreground mb-2 block">After</Label>
                    <ScrollArea className="h-[120px] rounded-lg bg-[#0A0A0B] border border-[#27272A] p-3">
                      <pre className="text-xs text-green-400 mono whitespace-pre-wrap">
                        {JSON.stringify(JSON.parse(selectedLog.after_json), null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogs;
